import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import {
  PalantirAvatarComponent,
  PalantirAvatarGroupComponent,
  PalantirBadgeComponent,
  PalantirIconComponent,
  PalantirTagComponent,
} from '@flux/design-system';
import type { ChatComponent } from '../../../models/chat.model';
import {
  asFluxListData,
  asFluxListPresentation,
  type FluxListAvatar,
  type FluxListBadge,
  type FluxListBadgeTheme,
} from '../../../models/flux-list.model';

/** Cores de `palantir-tag` usadas aqui — subconjunto de `PalantirLabelColors`. */
type FluxListTagColor = 'blue' | 'grey' | 'green' | 'pink' | 'orange' | 'red';

/** Linha pronta para o template (item + badges/avatares já resolvidos). */
interface FluxListRow {
  id: string;
  title: string;
  subtitle?: string;
  badges: FluxListBadge[];
  avatars: FluxListAvatar[];
  /** Presente só quando a entidade é abrível — a linha vira link (nova aba). */
  href?: string;
}

const TAG_COLORS: Record<FluxListBadgeTheme, FluxListTagColor> = {
  blue: 'blue',
  default: 'grey',
  green: 'green',
  magenta: 'pink',
  orange: 'orange',
  red: 'red',
};

/**
 * Renderer do componente `flux.list@1` emitido pela Iara: lista de entidades
 * (cards, fluxos, etapas…) com título, subtítulo, status (tag) e responsáveis
 * (avatares). Faz o narrowing seguro do envelope; payload inválido cai no
 * `fallbackText`. v1 é read-only — sem ações/paginação.
 */
@Component({
  selector: 'app-flux-list',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgTemplateOutlet,
    PalantirBadgeComponent,
    PalantirAvatarComponent,
    PalantirAvatarGroupComponent,
    PalantirIconComponent,
    PalantirTagComponent,
  ],
  templateUrl: './flux-list.component.html',
  styleUrl: './flux-list.component.scss',
})
export class FluxListComponent {
  public readonly component = input.required<ChatComponent>();

  protected readonly data = computed(() => asFluxListData(this.component().data));
  protected readonly presentation = computed(() =>
    asFluxListPresentation(this.component().presentation),
  );

  protected readonly title = computed(() => this.data()?.title ?? '');
  protected readonly showSubtitle = computed(() => this.presentation()?.showSubtitle ?? true);
  protected readonly compact = computed(() => this.presentation()?.density === 'compact');

  /** Contagem exibida no badge do cabeçalho: total conhecido ou itens carregados. */
  protected readonly count = computed(() => {
    const list = this.data();
    if (!list) return 0;
    return list.pagination.totalCount ?? list.items.length;
  });

  protected readonly rows = computed<FluxListRow[]>(() => {
    const list = this.data();
    if (!list) return [];

    const presentation = this.presentation();
    const badgesByItem = new Map<string, FluxListBadge[]>();
    for (const badge of presentation?.badges ?? []) {
      const current = badgesByItem.get(badge.itemId) ?? [];
      current.push(badge);
      badgesByItem.set(badge.itemId, current);
    }
    const avatarsByItem = presentation?.avatars ?? {};

    return list.items.map((item) => ({
      id: item.id,
      title: item.title,
      subtitle: item.subtitle,
      badges: badgesByItem.get(item.id) ?? [],
      avatars: avatarsByItem[item.id] ?? [],
      ...(item.canonicalUrl ? { href: item.canonicalUrl } : {}),
    }));
  });

  /** Rodapé "Mostrando X de Y" sempre que o total for conhecido. */
  protected readonly footer = computed(() => {
    const list = this.data();
    if (!list) return null;
    const total = list.pagination.totalCount;
    if (total == null) return null;
    return `Mostrando ${list.items.length} de ${total}`;
  });

  protected tagColor(theme: FluxListBadgeTheme): FluxListTagColor {
    return TAG_COLORS[theme];
  }
}
